/**
 * <uterific-popup> — the newsletter invitation, opened a few seconds after the visitor
 * starts reading.
 *
 * The countdown starts on the first scroll, not on load. Someone who has not moved yet has
 * not seen anything to sign up about, and interrupting them before they have is how a
 * popup earns a dismissal. Scrolling is the cheapest available signal that they are
 * actually engaging.
 *
 * Wraps a native <dialog> and uses showModal(), so the focus trap, the Escape key, the
 * inert background and the backdrop all come from the platform rather than from code here.
 *
 * It asks once. A dismissal, a signup, or a return visit after either is remembered in
 * localStorage, because a popup that reappears on every page of a browse is the reason
 * people distrust them. Storage is wrapped: a private window can throw on access, and the
 * popup opening one extra time is a far better failure than the page erroring.
 */

const REMEMBER = 'ut-popup-dismissed';

class UterificPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.onClose = () => this.remember();
    this.dialog.addEventListener('close', this.onClose);

    this.querySelectorAll('[data-ut-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.dialog.close());
    });

    /*
     * Shopify's spam check (the hCaptcha challenge) intercepts the form's submit and draws
     * its puzzle in the ordinary page stack. A modal dialog sits in the top layer, above
     * everything in that stack, so the puzzle appeared behind the popup and could not be
     * solved. The instant the form is submitted the dialog steps down out of the top
     * layer — closed as a modal, reopened in place — so the challenge lands on top of it.
     * Captured, so this runs before the challenge is drawn.
     */
    this.onSubmit = () => this.stepDown();
    this.dialog.addEventListener('submit', this.onSubmit, true);

    // Back from a submission: the page re-rendered with the form's result inside this
    // dialog (thanks, or an error to fix). Show it straight away, whatever was remembered.
    if (this.dialog.querySelector('.ut-waitlist__done, .ut-waitlist__error')) {
      this.open(true);
      return;
    }

    // In the theme editor the merchant is trying to look at this, so skip the wait and the
    // remembered dismissal entirely.
    if (this.hasAttribute('preview')) {
      this.open();
      return;
    }

    if (this.seen()) return;

    const delay = Number(this.getAttribute('delay'));
    this.delay = (Number.isFinite(delay) ? delay : 5) * 1000;

    /*
     * Captured on the document rather than bound to window: above 990px Horizon scrolls
     * .page-wrapper, not the window, and scroll events do not bubble — a window listener
     * would never fire on desktop and the popup would never open at all.
     */
    this.onFirstScroll = () => this.start();
    document.addEventListener('scroll', this.onFirstScroll, { passive: true, capture: true, once: true });
  }

  start() {
    if (this.timer || this.seen()) return;
    this.timer = window.setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    if (this.timer) window.clearTimeout(this.timer);
    document.removeEventListener('scroll', this.onFirstScroll, { capture: true });
    if (this.dialog) {
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('submit', this.onSubmit, true);
    }
  }

  open(force = false) {
    // Another dialog already has the top layer — a cart drawer, a search panel. Interrupting
    // something the visitor opened themselves is exactly the wrong moment to ask for an
    // email, so stand down rather than fight for it.
    if (!force && document.querySelector('dialog[open]')) return;
    if (this.dialog.open) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  stepDown() {
    if (!this.dialog.open || typeof this.dialog.show !== 'function') return;
    this.dialog.close();
    this.dialog.classList.add('ut-popup__dialog--stepped-down');
    this.dialog.show();
  }

  seen() {
    try {
      return window.localStorage.getItem(REMEMBER) === '1';
    } catch (err) {
      return false;
    }
  }

  remember() {
    try {
      window.localStorage.setItem(REMEMBER, '1');
    } catch (err) {
      /* private window, blocked storage — it will simply ask again next visit */
    }
  }
}

if (!customElements.get('uterific-popup')) {
  customElements.define('uterific-popup', UterificPopup);
}
